/**
 * @license Copyright (c) 2003-2019, CKSource - Frederico Knabben. All rights reserved.
 * For licensing, see LICENSE.md.
 */

import {
  Component,
  Input,
  Output,
  NgZone,
  EventEmitter,
  forwardRef,
  AfterViewInit,
  OnDestroy,
  ElementRef,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

import { ActiveMarkerInterface } from '../../model/plugin-decorator.model';
import { CKEditor5 } from './ckeditor';
import {
  BlurEvent,
  ChangeEvent,
  ChevronNavEvent,
  CitationEvent,
  CitationUndoRedoEvent,
  CitationsDeleteFromDocumentEvent,
  CitationsPanelReOrder,
  CitationsPanelReOrderOnSubRefDeletion,
  CommentStatusThreadIdEvent,
  CopyCitationNotificationEvent,
  DiscardCitationEvt,
  InsertLinkEvent,
  InsertLinkToAnotherDocumentEvent,
  InsertMediaEvent,
  InsertSubTemplateEvt,
  InsertWebEmailLinkEvent,
  MediaPropEvt,
  MediaSelectedEvt,
  NewCustomGuidEvt,
  OpenContextMenuEvt,
  PasteCitationRefsInDocument,
  ReOrderRightHandPanel,
  ReOrderUnNumberedReferencesEvent,
  RemoveMedia,
  RouteToLinkEvent,
  SaveEvent,
  UndoInterface,
  FocusEvent,
  RemoveInsertedCitationsOnDiscardEvent,
  InsertReferenceEvent,
  RemoveInsertedKeywordOnDiscardEvent,
  DiscardForKeyword,
  KeywordUndoRedoEvent,
  DeleteKeywordEvent,
  RemoveKeywordFromSubtemplateEvent,
  NumberedCitationInTableEvent,
  RemoveCommentsAndReleaseNotesEvent,
  MessageInfoEvent,
  ExternalPasteConfirmationRequiredEvent,
  ReorderedFinalReferencesEvent,
} from '../../model/custom-models';
import { MouseflowService } from '../../services/mouseflow.service';

@Component({
  selector: 'wk-ckeditor',
  template: '<ng-template></ng-template>',

  // Integration with @angular/forms.
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      // eslint-disable-next-line @typescript-eslint/no-use-before-define
      useExisting: forwardRef(() => CKEditorComponent),
      multi: true,
    },
  ],
})
export class CKEditorComponent
  implements AfterViewInit, OnDestroy, ControlValueAccessor
{
  /**
   * The reference to the DOM element created by the component.
   */
  private readonly elementRef!: ElementRef<HTMLElement>;

  /**
   * The constructor of the editor to be used for the instance of the component.
   * It can be e.g. the `ClassicEditorBuild`, `InlineEditorBuild` or some custom editor.
   */
  @Input() public editor?: CKEditor5.EditorConstructor;

  /**
   * The configuration of the editor.
   * See https://ckeditor.com/docs/ckeditor5/latest/api/module_core_editor_editorconfig-EditorConfig.html
   * to learn more.
   */
  @Input() public config: CKEditor5.Config = {};

  /**
   * The initial data of the editor. Useful when not using the ngModel.
   * See https://angular.io/api/forms/NgModel to learn more.
   */
  @Input() public data = '';

  /**
   * Tag name of the editor component.
   *
   * The default tag is 'div'.
   */
  @Input() public tagName = 'div';

  /**
   * When set `true`, the editor becomes read-only.
   * See https://ckeditor.com/docs/ckeditor5/latest/api/module_core_editor_editor-Editor.html#member-isReadOnly
   * to learn more.
   */
  @Input() public set disabled(isDisabled: boolean) {
    this.setDisabledState(isDisabled);
  }

  public get disabled() {
    if (this.editorInstance) {
      return this.editorInstance.isReadOnly;
    }

    return this.initialIsDisabled;
  }

  /**
   * Fires when the editor is ready. It corresponds with the `editor#ready`
   * https://ckeditor.com/docs/ckeditor5/latest/api/module_core_editor_editor-Editor.html#event-ready
   * event.
   */
  @Output() public ready = new EventEmitter<CKEditor5.Editor>();

  /**
   * Fires when the content of the editor has changed. It corresponds with the `editor.model.document#change`
   * https://ckeditor.com/docs/ckeditor5/latest/api/module_engine_model_document-Document.html#event-change
   * event.
   */
  @Output()
  public editorChange: EventEmitter<ChangeEvent> =
    new EventEmitter<ChangeEvent>();

  /**
   * Fires when the editing view of the editor is blurred. It corresponds with the `editor.editing.view.document#blur`
   * https://ckeditor.com/docs/ckeditor5/latest/api/module_engine_view_document-Document.html#event-event:blur
   * event.
   */
  @Output()
  public editorBlur: EventEmitter<BlurEvent> = new EventEmitter<BlurEvent>();

  /**
   * Fires when the editing view of the editor is focused. It corresponds with the `editor.editing.view.document#focus`
   * https://ckeditor.com/docs/ckeditor5/latest/api/module_engine_view_document-Document.html#event-event:focus
   * event.
   */
  @Output()
  public editorFocus: EventEmitter<FocusEvent> = new EventEmitter<FocusEvent>();

  /**
   * Fires when the editing view of the editor is focused. It corresponds with the `editor.editing.view.document#focus`
   * https://ckeditor.com/docs/ckeditor5/latest/api/module_engine_view_document-Document.html#event-event:focus
   * event.
   */
  @Output()
  public save: EventEmitter<SaveEvent> = new EventEmitter<SaveEvent>();

  @Output()
  public insertreleasenotes: EventEmitter<{
    id: string;
    isUMCReleaseNote: boolean;
  }> = new EventEmitter<{
    id: string;
    isUMCReleaseNote: boolean;
  }>();

  @Output()
  public discardCitation: EventEmitter<DiscardCitationEvt> =
    new EventEmitter<DiscardCitationEvt>();

  /**
   * Fired when citation icon in the citaiton toolbar is clicked
   */
  @Output()
  public citation: EventEmitter<CitationEvent> =
    new EventEmitter<CitationEvent>();

  /**
   * Fired when insert image icon in the  toolbar is clicked
   */
  @Output()
  public insertmedia: EventEmitter<InsertMediaEvent> =
    new EventEmitter<InsertMediaEvent>();

  /**
   * Fired when media element(image, audio and video) is clicked
   */

  @Output()
  public mediaselected: EventEmitter<MediaSelectedEvt> =
    new EventEmitter<MediaSelectedEvt>();

  /**
   * Fired when clicking on link insert
   */
  @Output()
  public insertlink: EventEmitter<InsertLinkEvent> =
    new EventEmitter<InsertLinkEvent>();

  @Output()
  public insertwebemaillink: EventEmitter<InsertWebEmailLinkEvent> =
    new EventEmitter<InsertWebEmailLinkEvent>();

  /**
   * Fired when Insert Subtemplate Button is clicked
   */
  @Output()
  public insertSubTemplate: EventEmitter<InsertSubTemplateEvt> =
    new EventEmitter<InsertSubTemplateEvt>();
  /**
   * Fired each time during citation undo/redo operation
   */
  @Output()
  public citationUndoRedo: EventEmitter<CitationUndoRedoEvent> =
    new EventEmitter<CitationUndoRedoEvent>();

  /**
   * Fired each time during keyword undo/redo operation
   */
  @Output()
  public keywordUndoRedo: EventEmitter<KeywordUndoRedoEvent> =
    new EventEmitter<KeywordUndoRedoEvent>();
  /**
   * Fired when Insert Subtemplate Button is clicked
   */
  @Output()
  public openContextMenu: EventEmitter<OpenContextMenuEvt> =
    new EventEmitter<OpenContextMenuEvt>();

  /**
   * Fired when SelectAll or partial Select is done
   */
  @Output()
  public mediaPropertiesNotification: EventEmitter<MediaPropEvt> =
    new EventEmitter<MediaPropEvt>();

  /* Fired when citation ref is deleted from document */

  @Output()
  public citationsDeleteFromDocument: EventEmitter<CitationsDeleteFromDocumentEvent> =
    new EventEmitter<CitationsDeleteFromDocumentEvent>();

  /* Fired with latest citation reordered data when citations are deleted via document */

  @Output()
  public citationsPanelReOrder: EventEmitter<CitationsPanelReOrder> =
    new EventEmitter<CitationsPanelReOrder>();

  @Output()
  public reOrderRightHandPanel: EventEmitter<ReOrderRightHandPanel> =
    new EventEmitter<ReOrderRightHandPanel>();

  @Output()
  public citationsPanelReOrderOnSubRefDeletion: EventEmitter<CitationsPanelReOrderOnSubRefDeletion> =
    new EventEmitter<CitationsPanelReOrderOnSubRefDeletion>();

  @Output()
  public activeMarker: EventEmitter<ActiveMarkerInterface> =
    new EventEmitter<ActiveMarkerInterface>();

  @Output()
  public addcommentthread: EventEmitter<any> = new EventEmitter<any>();

  @Output()
  public insertotherdocumentlink: EventEmitter<any> = new EventEmitter<any>();

  @Output()
  public routeToLink: EventEmitter<RouteToLinkEvent> =
    new EventEmitter<RouteToLinkEvent>();

  @Output()
  public removeInsertedCitationsOnDiscard: EventEmitter<RemoveInsertedCitationsOnDiscardEvent> =
    new EventEmitter<RemoveInsertedCitationsOnDiscardEvent>();

  @Output()
  public showMessage: EventEmitter<MessageInfoEvent> =
    new EventEmitter<MessageInfoEvent>();

  @Output()
  public pasteNotAllowed: EventEmitter<void> = new EventEmitter<void>();

  @Output()
  public pasteExternalConfirmationRequired: EventEmitter<ExternalPasteConfirmationRequiredEvent> =
    new EventEmitter<ExternalPasteConfirmationRequiredEvent>();

  @Output()
  public reorderKeywords: EventEmitter<void> = new EventEmitter<void>();

  @Output()
  public copyKeywordFromKeyboard: EventEmitter<any> = new EventEmitter<any>();
  /**
   * The instance of the editor created by this component.
   */
  public editorInstance: CKEditor5.Editor | null = null;

  /**
   * Fired when chevrons are clicked
   */
  @Output()
  public chevronNav: EventEmitter<ChevronNavEvent> =
    new EventEmitter<ChevronNavEvent>();

  @Output()
  public releasenotes: EventEmitter<any> = new EventEmitter<any>();

  /**
   * Fired when custom guid is generated
   */
  @Output()
  public newCustomGuid: EventEmitter<NewCustomGuidEvt> =
    new EventEmitter<NewCustomGuidEvt>();

  /**
   * Fired when citations Refs are pasted into the document
   */
  @Output()
  public pasteCitationRefsInDocument: EventEmitter<PasteCitationRefsInDocument> =
    new EventEmitter<PasteCitationRefsInDocument>();

  /**
   * Fired when CTRL + C is done and citation is copied to the clipboard
   */
  @Output()
  public copyCitationNotification: EventEmitter<CopyCitationNotificationEvent> =
    new EventEmitter<CopyCitationNotificationEvent>();

  @Output()
  public commentStatusThreadId: EventEmitter<CommentStatusThreadIdEvent> =
    new EventEmitter<CommentStatusThreadIdEvent>();

  @Output()
  public removeMedia: EventEmitter<RemoveMedia> =
    new EventEmitter<RemoveMedia>();

  /**
   * Fired on click of a text if its linked to another document
   */
  @Output()
  public insertlinktoanotherdocument: EventEmitter<InsertLinkToAnotherDocumentEvent> =
    new EventEmitter<InsertLinkToAnotherDocumentEvent>();

  @Output()
  public reorderunnumberedreferences: EventEmitter<ReOrderUnNumberedReferencesEvent> =
    new EventEmitter<ReOrderUnNumberedReferencesEvent>();

  @Output()
  public undoRedoExecuted: EventEmitter<UndoInterface> =
    new EventEmitter<UndoInterface>();

  /**
   * Fired when clicking on  Insert cross-references
   */
  @Output()
  public insertreference: EventEmitter<InsertReferenceEvent> =
    new EventEmitter<InsertReferenceEvent>();

  /**
   * Fired when clicking on  Discard of Keyword
   */
  @Output()
  public removeInsertedKeywordOnDiscard: EventEmitter<RemoveInsertedKeywordOnDiscardEvent> =
    new EventEmitter<RemoveInsertedKeywordOnDiscardEvent>();

  @Output()
  public removeMediaCaption: EventEmitter<RemoveCommentsAndReleaseNotesEvent> =
    new EventEmitter<RemoveCommentsAndReleaseNotesEvent>();
  /**
   * Fired when clicking on accept of Keyword with subtemplae
   */
  @Output()
  public removeKeywordFromSubtemplate: EventEmitter<RemoveKeywordFromSubtemplateEvent> =
    new EventEmitter<RemoveKeywordFromSubtemplateEvent>();

  /* Fired when keyword ref is deleted from document */
  @Output()
  public keywordDeleteFromDocument: EventEmitter<DeleteKeywordEvent> =
    new EventEmitter<DeleteKeywordEvent>();

  @Output()
  public checkNumberedCitationInTable: EventEmitter<NumberedCitationInTableEvent> =
    new EventEmitter<NumberedCitationInTableEvent>();

  @Output()
  public reorderedfinalreferences: EventEmitter<ReorderedFinalReferencesEvent> =
    new EventEmitter<ReorderedFinalReferencesEvent>();
  /**
   * If the component is read–only before the editor instance is created, it remembers that state,
   * so the editor can become read–only once it is ready.
   */
  private initialIsDisabled = false;

  /**
   * An instance of https://angular.io/api/core/NgZone to allow the interaction with the editor
   * withing the Angular event loop.
   */
  private readonly ngZone: NgZone;

  /**
   * A callback executed when the content of the editor changes. Part of the
   * `ControlValueAccessor` (https://angular.io/api/forms/ControlValueAccessor) interface.
   *
   * Note: Unset unless the component uses the `ngModel`.
   */
  private cvaOnChange?: (data: string) => void;

  /**
   * A callback executed when the editor has been blurred. Part of the
   * `ControlValueAccessor` (https://angular.io/api/forms/ControlValueAccessor) interface.
   *
   * Note: Unset unless the component uses the `ngModel`.
   */
  private cvaOnTouched?: () => void;

  /**
   * Reference to the source element used by the editor.
   */
  private editorElement?: HTMLElement;

  private readonly ckeditor5ReadOnlyId = Symbol('ckeditor5ReadOnlyId');

  public constructor(
    elementRef: ElementRef,
    ngZone: NgZone,
    private readonly mouseflowService: MouseflowService
  ) {
    this.ngZone = ngZone;
    this.elementRef = elementRef;
  }

  // Implementing the AfterViewInit interface.
  public ngAfterViewInit() {
    // Notify Mouseflow that a new "page" (the editor view) is active so it
    // can generate a dedicated heatmap for this component.
    this.mouseflowService.newPageView('/editor');
    this.ngZone.runOutsideAngular(() => {
      this.createEditor();
    });
  }

  // Implementing the OnDestroy interface.
  public ngOnDestroy() {
    if (this.editorInstance) {
      this.editorInstance.destroy();
      this.editorInstance = null;
    }
  }

  // Implementing the ControlValueAccessor interface (only when binding to ngModel).
  public writeValue(value: string | null): void {
    // This method is called with the `null` value when the form resets.
    // A component's responsibility is to restore to the initial state.
    if (value === null) {
      value = '';
    }

    // If already initialized.
    if (this.editorInstance) {
      this.editorInstance.setData(value);
    } else {
      // If not, wait for it to be ready; store the data.
      this.data = value;

      // If the editor element is already available, then update its content.
      // If the ngModel is used then the editor element should be updated directly here.
      if (this.editorElement) {
        this.editorElement.innerHTML = this.data;
      }
    }
  }

  // Implementing the ControlValueAccessor interface (only when binding to ngModel).
  public registerOnChange(callback: (data: string) => void): void {
    this.cvaOnChange = callback;
  }

  // Implementing the ControlValueAccessor interface (only when binding to ngModel).
  public registerOnTouched(callback: () => void): void {
    this.cvaOnTouched = callback;
  }

  // Implementing the ControlValueAccessor interface (only when binding to ngModel).
  public setDisabledState(isDisabled: boolean): void {
    // If already initialized
    if (this.editorInstance) {
      isDisabled
        ? this.editorInstance.enableReadOnlyMode(this.ckeditor5ReadOnlyId)
        : this.editorInstance.disableReadOnlyMode(this.ckeditor5ReadOnlyId);
    } else {
      // If not, wait for it to be ready; store the state.
      this.initialIsDisabled = isDisabled;
    }
  }

  /**
   * Creates the editor instance, sets initial editor data, then integrates
   * the editor with the Angular component. This method does not use the `editor.setData()`
   * because of the issue in the collaboration mode (#6).
   */
  private createEditor(): Promise<void> {
    const element = document.createElement(this.tagName);
    this.editorElement = element;

    if (this.data && this.config.initialData) {
      throw new Error(
        'Editor data should be provided either using `config.initialData` or `data` properties.'
      );
    }

    // Merge two possible ways of providing data into the `config.initialData` field.
    const config = {
      ...this.config,
      initialData: this.config.initialData || this.data || '',
    };

    this.elementRef.nativeElement.appendChild(element);

    return this.editor
      .create(element, config)
      .then((editor) => {
        this.editorInstance = editor;

        this.editorInstance.on(
          'change:isReadOnly',
          (_evt: any, _propertyName: any, isReadOnly: any) => {
            if (isReadOnly) {
              this.editorInstance.ui.view.toolbar.element.style.display =
                'none';
            } else {
              this.editorInstance.ui.view.toolbar.element.style.display =
                'flex';
            }
          }
        );

        if (this.initialIsDisabled) {
          this.editorInstance.enableReadOnlyMode(this.ckeditor5ReadOnlyId);
        }

        // Tell Mouseflow not to mask the editable area so document
        // content is fully visible in session replays.
        const editableElement = editor.editing?.view?.getDomRoot();
        if (editableElement) {
          this.mouseflowService.unmasElement(editableElement);
        }

        this.ngZone.run(() => {
          this.ready.emit(editor);
        });

        this.setUpEditorEvents(editor);
      })
      .catch((err: Error) => {
        console.error(err.stack);
      });
  }

  /**
   * Integrates the editor with the component by attaching related event listeners.
   */
  private setUpEditorEvents(editor: CKEditor5.Editor): void {
    const modelDocument = editor.model.document;
    const viewDocument = editor.editing.view.document;

    modelDocument.on(
      'change:data',
      (evt: CKEditor5.EventInfo<'change:data'>) => {
        this.ngZone.run(() => {
          if (this.cvaOnChange) {
            const data = editor.getData();

            this.cvaOnChange(data);
          }
          this.editorChange.emit({ event: evt, editor });
        });
      }
    );

    viewDocument.on('focus', (evt: CKEditor5.EventInfo<'focus'>) => {
      this.ngZone.run(() => {
        this.editorFocus.emit({ event: evt, editor });
      });
    });

    viewDocument.on('blur', (evt: CKEditor5.EventInfo<'blur'>) => {
      this.ngZone.run(() => {
        if (this.cvaOnTouched) {
          this.cvaOnTouched();
        }
        this.editorBlur.emit({ event: evt, editor });
      });
    });

    (editor as any)?.on('save', (evt: CKEditor5.EventInfo<'save'>) => {
      this.ngZone.run(() => {
        this.save.emit({ event: evt, editor });
      });
    });

    (editor as any)?.on('citation', (evt: CKEditor5.EventInfo<'citation'>) => {
      this.ngZone.run(() => {
        this.citation.emit({ event: evt, editor });
      });
    });

    (editor as any)?.on(
      'chevronnav',
      (evt: CKEditor5.EventInfo<'chevronnav'>, val) => {
        this.ngZone.run(() => {
          this.chevronNav.emit({
            event: evt,
            id: val.value.id,
            citationCount: val.value.citationCount,
            editor,
          });
        });
      }
    );

    (editor as any)?.on('releasenotecreated', () => {
      this.ngZone.run(() => {
        this.releasenotes.emit();
      });
    });

    (editor as any)?.on(
      'insertmedia',
      (evt: CKEditor5.EventInfo<'insertmedia'>, val) => {
        this.ngZone.run(() => {
          this.insertmedia.emit({ event: evt, editor, name: val });
        });
      }
    );

    (editor as any)?.on(
      'insertlink',
      (_: any, val: any, linkAttributes: any) => {
        this.ngZone.run(() => {
          this.insertlink.emit({ editor, arguments: val, linkAttributes });
        });
      }
    );

    (editor as any)?.on('insertwebemaillink', () => {
      this.ngZone.run(() => {
        this.insertwebemaillink.emit({ value: true });
      });
    });

    (editor as any)?.on(
      'insertSubTemplate',
      (evt: CKEditor5.EventInfo<'insertSubTemplate'>, val) => {
        this.ngZone.run(() => {
          this.insertSubTemplate.emit({
            event: evt,
            name: val,
            editor,
          });
        });
      }
    );
    (editor as any)?.on(
      'mediaselected',
      (evt: CKEditor5.EventInfo<'mediaselected'>, val) => {
        this.ngZone.run(() => {
          this.mediaselected.emit({
            event: evt,
            mediaObj: val,
            editor,
          });
        });
      }
    );
    (editor as any)?.on(
      'openContextMenu',
      (evt: CKEditor5.EventInfo<'openContextMenu'>, val) => {
        this.ngZone.run(() => {
          this.openContextMenu.emit({
            event: evt,
            contextMenuObj: val,
            editor,
          });
        });
      }
    );

    (editor as any)?.on(
      'mediaPropertiesNotification',
      (evt: CKEditor5.EventInfo<'mediaPropertiesNotification'>, val) => {
        this.ngZone.run(() => {
          this.mediaPropertiesNotification.emit({
            event: evt,
            value: val,
            editor,
          });
        });
      }
    );

    (editor as any)?.on(
      'citationsPanelReOrder',
      (evt: CKEditor5.EventInfo<'citationsPanelReOrder'>, data) => {
        this.ngZone.run(() => {
          this.citationsPanelReOrder.emit({
            event: evt,
            citationsPanelReOrderData: data.citationsPanelReOrderData,
            id: data.id,
            editor,
          });
        });
      }
    );

    (editor as any)?.on(
      'citationsDeleteFromDocument',
      (evt: CKEditor5.EventInfo<'citationsDeleteFromDocument'>, data) => {
        this.ngZone.run(() => {
          this.citationsDeleteFromDocument.emit({
            event: evt,
            multipleRefs: data.multipleRefs,
            isCut: data.isCut || false,
            editor,
          });
        });
      }
    );

    (editor as any)?.on(
      'newcustomguid',
      (evt: CKEditor5.EventInfo<'newCustomGuid'>, data) => {
        this.ngZone.run(() => {
          this.newCustomGuid.emit({
            event: evt,
            editor,
            id: data.id,
          });
        });
      }
    );

    (editor as any)?.on(
      'reOrderRightHandPanel',
      (evt: CKEditor5.EventInfo<'reOrderRightHandPanel'>) => {
        this.ngZone.run(() => {
          this.reOrderRightHandPanel.emit({
            event: evt,
            editor,
          });
        });
      }
    );

    (editor as any)?.on(
      'citationsPanelReOrderOnSubRefDeletion',
      (evt: CKEditor5.EventInfo<'citationsPanelReOrderOnSubRefDeletion'>) => {
        this.ngZone.run(() => {
          this.citationsPanelReOrderOnSubRefDeletion.emit({
            event: evt,
            editor,
          });
        });
      }
    );

    (editor as any)?.on(
      'discardCitation',
      (evt: CKEditor5.EventInfo<'discardCitation'>, val) => {
        this.ngZone.run(() => {
          this.discardCitation.emit({
            event: evt,
            value: val,
            editor,
          });
        });
      }
    );
    this.setUpEditorEventHelperFunc(editor);
    this.setUpEditorEventsForKeywords(editor);
  }

  setUpEditorEventsForKeywords(editor) {
    editor?.on('insertreference', (_: any, val: any, refAttributes: any) => {
      this.ngZone.run(() => {
        this.insertreference.emit({ editor, arguments: val, refAttributes });
      });
    });

    editor?.on(
      'reorderKeywords',
      (_: CKEditor5.EventInfo<'reorderKeywords'>) => {
        this.ngZone.run(() => {
          this.reorderKeywords.emit();
        });
      }
    );

    editor?.on(
      'copyKeywordFromKeyboard',
      (_: CKEditor5.EventInfo<'copyKeywordFromKeyboard'>, val) => {
        this.ngZone.run(() => {
          this.copyKeywordFromKeyboard.emit({ arguments: val });
        });
      }
    );

    editor?.on(
      'keywordDeleteFromDocument',
      (evt: CKEditor5.EventInfo<'keywordDeleteFromDocument'>, data) => {
        this.ngZone.run(() => {
          this.keywordDeleteFromDocument.emit({
            event: evt,
            keywordRefs: data.keywordRefs,
            unwrapKeyword: data.unwrapKeyword,
            isEnabledDialog: data.isEnabledDialog,
            editor,
            selectedKeywordId: data.selectedKeywordId,
          });
        });
      }
    );

    editor?.on(
      'keywordUndoRedo',
      (
        _: CKEditor5.EventInfo<'keywordUndoRedo'>,
        val: KeywordUndoRedoEvent
      ) => {
        this.ngZone.run(() => {
          this.keywordUndoRedo.emit({
            operation: val.operation,
            keywordToUndo: val.keywordToUndo,
            keywordToRedo: val.keywordToRedo,
          });
        });
      }
    );

    editor?.on(
      'removeKeywordFromSubtemplate',
      (
        _: CKEditor5.EventInfo<'removeKeywordFromSubtemplate'>,
        val: RemoveKeywordFromSubtemplateEvent
      ) => {
        this.ngZone.run(() => {
          this.removeKeywordFromSubtemplate.emit({
            resourceId: val.resourceId,
            keywordId: val.keywordId,
          });
        });
      }
    );
  }

  setUpEditorEventHelperFunc(editor) {
    editor?.on(
      'pasteCitationRefsInDocument',
      (evt: CKEditor5.EventInfo<'pasteCitationRefsInDocument'>, data) => {
        this.ngZone.run(() => {
          this.pasteCitationRefsInDocument.emit({
            event: evt,
            editor,
            refsToBeReOrderedInRightHandPanel: data.citationsPanelReOrderData,
          });
        });
      }
    );

    editor?.on(
      'commentStatusThreadId',
      (_evt: CKEditor5.EventInfo<'commentStatusThreadId'>, data) => {
        this.ngZone.run(() => {
          this.commentStatusThreadId.emit({
            commentStatusThreadIdStatus: data.value,
          });
        });
      }
    );

    editor?.on(
      'activemarker',
      (evt: CKEditor5.EventInfo<'activemarker'>, data) => {
        this.ngZone.run(() => {
          this.activeMarker.emit({
            event: evt,
            editor,
            data,
          });
        });
      }
    );
    editor?.on(
      'copyCitationNotification',
      (evt: CKEditor5.EventInfo<'copyCitationNotification'>) => {
        this.ngZone.run(() => {
          this.copyCitationNotification.emit({ event: evt, editor });
        });
      }
    );
    editor?.on(
      'addcommentthread',
      (evt: CKEditor5.EventInfo<'addcommentthread'>, data) => {
        this.ngZone.run(() => {
          this.addcommentthread.emit({
            event: evt,
            editor,
            data,
          });
        });
      }
    );

    editor?.on('insertreleasenotes', (_evt, data) => {
      this.ngZone.run(() => {
        this.insertreleasenotes.emit({
          id: data.threadId,
          isUMCReleaseNote: data.isUMCReleaseNote,
        });
      });
    });

    editor?.on(
      'insertotherdocumentlink',
      (evt: CKEditor5.EventInfo<'insertotherdocumentlink'>, data) => {
        this.ngZone.run(() => {
          this.insertotherdocumentlink.emit({
            evt,
            idlink: data,
          });
        });
      }
    );
    editor?.on(
      'routeToLink',
      (evt: CKEditor5.EventInfo<'routeToLink'>, val) => {
        this.ngZone.run(() => {
          this.routeToLink.emit({
            event: evt,
            linkAttributes: val,
          });
        });
      }
    );
    editor?.on(
      'insertlinktoanotherdocument',
      (evt: CKEditor5.EventInfo<'insertlinktoanotherdocument'>, val) => {
        this.ngZone.run(() => {
          this.insertlinktoanotherdocument.emit({
            event: evt,
            editor,
            linkAttributes: val,
          });
        });
      }
    );
    editor?.on(
      'citationundoredo',
      (evt: CKEditor5.EventInfo<'citationundoredo'>, val) => {
        this.ngZone.run(() => {
          this.citationUndoRedo.emit({
            event: evt,
            editor,
            linkAttributes: val,
          });
          editor.commands.get('citation').isEnabled = true;
        });
      }
    );

    editor?.on(
      'reorderunnumberedreferences',
      (evt: CKEditor5.EventInfo<'reorderunnumberedreferences'>) => {
        this.ngZone.run(() => {
          this.reorderunnumberedreferences.emit({
            event: evt,
            editor,
          });
        });
      }
    );

    editor?.on('removemedia', (evt: CKEditor5.EventInfo<'removemedia'>) => {
      this.ngZone.run(() => {
        this.removeMedia.emit({
          event: evt,
          editor,
        });
      });
    });

    editor?.on(
      'undoRedoExecuted',
      (evt: CKEditor5.EventInfo<'undoRedoExecuted'>) => {
        this.ngZone.run(() => {
          this.undoRedoExecuted.emit({
            event: evt,
            editor,
          });
        });
      }
    );

    editor?.on(
      'removeInsertedCitationsOnDiscard',
      (evt: CKEditor5.EventInfo<'removeInsertedCitationsOnDiscard'>) => {
        this.ngZone.run(() => {
          this.removeInsertedCitationsOnDiscard.emit({
            event: evt,
          });
        });
      }
    );

    editor?.on(
      'removeInsertedKeywordOnDiscard',
      (
        evt: CKEditor5.EventInfo<'removeInsertedKeywordOnDiscard'>,
        val: DiscardForKeyword
      ) => {
        this.ngZone.run(() => {
          this.removeInsertedKeywordOnDiscard.emit({
            event: evt,
            docRefId: val.docReferenceId,
            keywordId: val.keywordId,
          });
        });
      }
    );

    editor?.on(
      'showMessage',
      (
        _: CKEditor5.EventInfo<'showMessage'>,
        val: {
          isBulletListCopyMessage?: boolean;
          isBulletListCutMessage?: boolean;
        }
      ) => {
        this.ngZone.run(() => {
          this.showMessage.emit({
            isBulletListCopyMessage: val?.isBulletListCopyMessage || false,
            isBulletListCutMessage: val?.isBulletListCutMessage || false,
          });
        });
      }
    );

    editor?.on(
      'removeMediaCaption',
      (_: CKEditor5.EventInfo<'removeMediaCaption'>, val) => {
        this.ngZone.run(() => {
          this.removeMediaCaption.emit({ range: val.range });
        });
      }
    );

    editor?.on(
      'pasteNotAllowed',
      (_: CKEditor5.EventInfo<'pasteNotAllowed'>) => {
        this.ngZone.run(() => {
          this.pasteNotAllowed.emit();
        });
      }
    );

    editor?.on(
      'pasteExternalConfirmationRequired',
      (
        _: CKEditor5.EventInfo<'pasteExternalConfirmationRequired'>,
        val: ExternalPasteConfirmationRequiredEvent
      ) => {
        this.ngZone.run(() => {
          this.pasteExternalConfirmationRequired.emit({
            plainText: val?.plainText || '',
            pasteSource: val?.pasteSource || '',
          });
        });
      }
    );

    editor?.on(
      'checkNumberedCitationInTable',
      (_: CKEditor5.EventInfo<'checkNumberedCitationInTable'>, val) => {
        this.ngZone.run(() => {
          this.checkNumberedCitationInTable.emit({
            event: val.evt,
            selectedElement: val.selectedElement,
          });
        });
      }
    );

    editor?.on(
      'reorderedfinalreferences',
      (_: CKEditor5.EventInfo<'reorderedfinalreferences'>, val) => {
        this.ngZone.run(() => {
          this.reorderedfinalreferences.emit({
            idsOfFirstCitedRefs: val.idsOfFirstCitedRefs,
          });
        });
      }
    );
  }
}
